import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, RefreshCw, Trash2, Shield, Activity } from 'lucide-react';

interface BlockLog {
  id: string;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  reason: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

interface AggregateRow {
  key: string;
  label: string;
  count: number;
  lastSeen: string;
  reasons: Record<string, number>;
}

export const SpamMonitoringSection = () => {
  const { toast } = useToast();
  const [logs, setLogs] = useState<BlockLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('registration_block_log' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      setLogs((data as unknown as BlockLog[]) || []);
    } catch (e: any) {
      toast({
        title: '\u054d\u056d\u0561\u056c',
        description: e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const clearAll = async () => {
    if (!confirm('\u054b\u0576\u057b\u0565\u056c \u0562\u0578\u056c\u0578\u0580 \u0563\u0580\u0561\u057c\u0578\u0582\u0574\u0576\u0565\u0580\u0568?')) return;
    try {
      const { error } = await supabase
        .from('registration_block_log' as any)
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000');
      if (error) throw error;
      toast({ title: '\u053f\u0561\u057f\u0561\u0580\u057e\u0561\u056e \u0567' });
      fetchLogs();
    } catch (e: any) {
      toast({ title: '\u054d\u056d\u0561\u056c', description: e.message, variant: 'destructive' });
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter(
      (l) =>
        (l.email || '').toLowerCase().includes(q) ||
        (l.phone || '').toLowerCase().includes(q) ||
        (l.ip_address || '').toLowerCase().includes(q) ||
        `${l.first_name || ''} ${l.last_name || ''}`.toLowerCase().includes(q)
    );
  }, [logs, search]);

  const aggregate = (key: keyof BlockLog): AggregateRow[] => {
    const map = new Map<string, AggregateRow>();
    for (const log of filtered) {
      const value = (log[key] as string | null) || '\u2014';
      const existing = map.get(value);
      if (existing) {
        existing.count++;
        existing.reasons[log.reason] = (existing.reasons[log.reason] || 0) + 1;
        if (log.created_at > existing.lastSeen) existing.lastSeen = log.created_at;
      } else {
        map.set(value, {
          key: value,
          label: value,
          count: 1,
          lastSeen: log.created_at,
          reasons: { [log.reason]: 1 },
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  };

  const byIp = aggregate('ip_address');
  const byEmail = aggregate('email');
  const byPhone = aggregate('phone');

  const total = filtered.length;
  const last24h = filtered.filter(
    (l) => Date.now() - new Date(l.created_at).getTime() < 24 * 60 * 60 * 1000
  ).length;
  const uniqueIps = new Set(filtered.map((l) => l.ip_address).filter(Boolean)).size;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        <Card className="border-border/70 bg-card/80">
          <CardContent className="p-4 flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <div className="text-2xl font-bold text-foreground">{total}</div>
              <div className="text-xs text-muted-foreground/70">{"\u0568\u0576\u0564\u0570\u0561\u0576\u0578\u0582\u0580 \u0561\u0580\u0563\u0565\u056c\u0561\u0583\u0561\u056f\u0578\u0582\u0574\u0576\u0565\u0580"}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/70 bg-card/80">
          <CardContent className="p-4 flex items-center gap-3">
            <Activity className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            <div>
              <div className="text-2xl font-bold text-foreground">{last24h}</div>
              <div className="text-xs text-muted-foreground/70">{"\u057e\u0565\u0580\u057b\u056b\u0576 24 \u056a\u0561\u0574"}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/70 bg-card/80">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <div>
              <div className="text-2xl font-bold text-foreground">{uniqueIps}</div>
              <div className="text-xs text-muted-foreground/70">{"\u0578\u0582\u0576\u056b\u056f\u0561\u056c IP \u0570\u0561\u057d\u0581\u0565\u0576\u0565\u0580"}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/70 bg-card/80">
        <CardHeader className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <CardTitle className="text-foreground">{"\u054d\u057a\u0561\u0574 \u0574\u0578\u0576\u056b\u0569\u0578\u0580\u056b\u0576\u0563"}</CardTitle>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button size="sm" variant="outline" className="min-h-11 w-full sm:w-auto" onClick={fetchLogs} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              {"\u0539\u0561\u0580\u0574\u0561\u0581\u0576\u0565\u056c"}
            </Button>
            <Button size="sm" variant="destructive" className="min-h-11 w-full sm:w-auto" onClick={clearAll}>
              <Trash2 className="h-4 w-4 mr-1" />
              {"\u0544\u0561\u0584\u0580\u0565\u056c \u0562\u0578\u056c\u0578\u0580\u0568"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <Input
            placeholder={"\u0548\u0580\u0578\u0576\u0565\u056c \u0567\u056c. \u0583\u0578\u057d\u057f\u0578\u057e, \u0570\u0565\u057c., IP \u056f\u0561\u0574 \u0561\u0576\u0578\u0582\u0576\u0578\u057e..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-4 min-h-11 border-border/70 bg-background text-foreground"
          />

          <Tabs defaultValue="ip" className="w-full">
            <div className="-mx-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList className="inline-flex h-auto min-w-max gap-1 border border-border/70 bg-muted/70 p-1.5">
              <TabsTrigger value="ip">{"\u054020 IP"}</TabsTrigger>
              <TabsTrigger value="email">{"\u054020 Email"}</TabsTrigger>
              <TabsTrigger value="phone">{"\u054020 \u0540\u0565\u057c."}</TabsTrigger>
              <TabsTrigger value="raw">{"\u0531\u0574\u0562\u0578\u0572\u057b \u056c\u0578\u0563"}</TabsTrigger>
            </TabsList>
            </div>

            <TabsContent value="ip"><AggregateTable rows={byIp.slice(0, 20)} /></TabsContent>
            <TabsContent value="email"><AggregateTable rows={byEmail.slice(0, 20)} /></TabsContent>
            <TabsContent value="phone"><AggregateTable rows={byPhone.slice(0, 20)} /></TabsContent>

            <TabsContent value="raw">
              <div className="max-h-[600px] overflow-auto rounded-md border border-border/70">
                <Table className="min-w-[820px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-muted-foreground">{"\u0531\u0574\u057d\u0561\u0569\u056b\u057e"}</TableHead>
                      <TableHead className="text-muted-foreground">{"\u0531\u0576\u0578\u0582\u0576"}</TableHead>
                      <TableHead className="text-muted-foreground">Email</TableHead>
                      <TableHead className="text-muted-foreground">{"\u0540\u0565\u057c."}</TableHead>
                      <TableHead className="text-muted-foreground">IP</TableHead>
                      <TableHead className="text-muted-foreground">{"\u054a\u0561\u057f\u0573\u0561\u057c"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell className="text-xs text-muted-foreground/80">
                          {new Date(l.created_at).toLocaleString('hy-AM')}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {`${l.first_name || ''} ${l.last_name || ''}`.trim() || '\u2014'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{l.email || '\u2014'}</TableCell>
                        <TableCell className="text-muted-foreground">{l.phone || '\u2014'}</TableCell>
                        <TableCell className="text-xs text-amber-600 dark:text-amber-400">{l.ip_address || '\u2014'}</TableCell>
                        <TableCell>
                          <Badge variant={l.reason === 'LIMIT_REACHED' ? 'destructive' : 'secondary'}>
                            {l.reason}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground/60 py-8">
                          {loading ? '\u0532\u0565\u057c\u0576\u057e\u0578\u0582\u0574 \u0567...' : '\u0533\u0580\u0561\u057c\u0578\u0582\u0574\u0576\u0565\u0580 \u0579\u056f\u0561\u0576'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

const AggregateTable = ({ rows }: { rows: AggregateRow[] }) => (
  <div className="overflow-x-auto rounded-md border border-border/70">
    <Table className="min-w-[640px]">
      <TableHeader>
        <TableRow>
          <TableHead className="text-muted-foreground">{"\u0531\u0580\u056a\u0565\u0584"}</TableHead>
          <TableHead className="text-muted-foreground text-right">{"\u0539\u056b\u057e"}</TableHead>
          <TableHead className="text-muted-foreground">{"\u054a\u0561\u057f\u0573\u0561\u057c\u0576\u0565\u0580"}</TableHead>
          <TableHead className="text-muted-foreground">{"\u054e\u0565\u0580\u057b\u056b\u0576"}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.key}>
            <TableCell className="text-foreground">{r.label}</TableCell>
            <TableCell className="text-right text-muted-foreground font-bold">{r.count}</TableCell>
            <TableCell>
              <div className="flex gap-1 flex-wrap">
                {Object.entries(r.reasons).map(([k, v]) => (
                  <Badge key={k} variant={k === 'LIMIT_REACHED' ? 'destructive' : 'secondary'}>
                    {k}: {v}
                  </Badge>
                ))}
              </div>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground/70">
              {new Date(r.lastSeen).toLocaleString('hy-AM')}
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={4} className="text-center text-muted-foreground/60 py-6">
              {"\u0533\u0580\u0561\u057c\u0578\u0582\u0574\u0576\u0565\u0580 \u0579\u056f\u0561\u0576"}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </div>
);

export default SpamMonitoringSection;
